import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Checkbox, Select } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/app/Toast";
import { useAuth } from "@/app/AuthContext";

interface LocationRow { id: number; name: string; }

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  source: string;
  marketingOptIn: boolean;
  notesSummary: string;
  vehicle: {
    plate: string;
    make: string;
    model: string;
    color: string;
    vehicleType: string;
  };
}

const INITIAL: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  source: "walk-in",
  marketingOptIn: false,
  notesSummary: "",
  vehicle: { plate: "", make: "", model: "", color: "", vehicleType: "" },
};

export function NewCustomerPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [homeLocationId, setHomeLocationId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get<LocationRow[]>("/api/v1/locations").then((rows) => {
      const manageable = user?.role === "ADMIN"
        ? rows
        : rows.filter((location) => user?.memberships.some((membership) => membership.locationId === location.id && membership.scope === "MANAGE"));
      setLocations(manageable);
      setHomeLocationId((current) => manageable.some((location) => location.id === current) ? current : manageable[0]?.id ?? null);
    }).catch((error) => toast("error", error instanceof Error ? error.message : "Could not load locations"));
  }, [toast, user]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.email && !form.phone) {
      toast("error", "At least email or phone is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ id: number }>("/api/v1/customers", {
        firstName: form.firstName,
        homeLocationId,
        lastName: form.lastName,
        email: form.email || null,
        phone: form.phone || null,
        source: form.source || null,
        marketingOptIn: form.marketingOptIn,
        notesSummary: form.notesSummary || null,
      });
      const customerId = res.id;
      if (form.vehicle.plate) {
        await api.post(`/api/v1/customers/${customerId}/vehicles`, {
          plate: form.vehicle.plate,
          make: form.vehicle.make || null,
          model: form.vehicle.model || null,
          color: form.vehicle.color || null,
          vehicleType: form.vehicle.vehicleType || null,
        });
      }
      toast("success", `Customer #${customerId} created`);
      nav(`/customers/${customerId}`);
    } catch (err) {
      toast("error", apiErrorMessage(err, "Save failed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New customer</h1>
      <form onSubmit={submit} className="space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field className="sm:col-span-2">
              <Label>Home location</Label>
              <Select required value={homeLocationId?.toString() ?? ""} onChange={(event) => setHomeLocationId(Number(event.target.value))}>
                <option value="" disabled>Select a location…</option>
                {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
              </Select>
            </Field>
            <Field>
              <Label>First name</Label>
              <Input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </Field>
            <Field>
              <Label>Last name</Label>
              <Input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </Field>
            <Field>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field>
              <Label>Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="walk-in, public, referral, import…" />
            </Field>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox
                id="opt-in"
                checked={form.marketingOptIn}
                onChange={(e) => setForm({ ...form, marketingOptIn: e.currentTarget.checked })}
              />
              <Label htmlFor="opt-in">Marketing opt-in</Label>
            </div>
            <Field className="sm:col-span-2">
              <Label>Notes</Label>
              <Input value={form.notesSummary} onChange={(e) => setForm({ ...form, notesSummary: e.target.value })} />
            </Field>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">First vehicle (optional)</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field>
              <Label>Plate</Label>
              <Input
                value={form.vehicle.plate}
                onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, plate: e.target.value } })}
                placeholder="optional"
              />
            </Field>
            <Field>
              <Label>Make</Label>
              <Input value={form.vehicle.make} onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, make: e.target.value } })} />
            </Field>
            <Field>
              <Label>Model</Label>
              <Input value={form.vehicle.model} onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, model: e.target.value } })} />
            </Field>
            <Field>
              <Label>Color</Label>
              <Input value={form.vehicle.color} onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, color: e.target.value } })} />
            </Field>
            <Field>
              <Label>Type</Label>
              <Input
                value={form.vehicle.vehicleType}
                onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, vehicleType: e.target.value } })}
                placeholder="sedan | suv | van | truck | bike"
              />
            </Field>
          </CardContent>
        </Card>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
          <Button type="submit" disabled={submitting || !homeLocationId}>{submitting ? "Saving…" : "Create customer"}</Button>
        </div>
      </form>
    </div>
  );
}

const Field: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn(className, "space-y-1.5")}>{children}</div>
);

import { cn } from "@/lib/utils";
